<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { getQuantileIntervals } from '@chatwoot/utils';
import reportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import BarChart from 'shared/components/charts/BarChart.vue';
import {
  buildDatasetHoverDimmingHandler,
  buildIntercomLikeBarTooltip,
  removeTooltipEl,
} from 'shared/components/charts/barInteractions';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';
import { DATE_RANGE_OPTIONS } from './constants';

const METRIC_KEY = 'missed_chats_count';
const HEATMAP_QUANTILES = [0.2, 0.4, 0.6, 0.8, 0.9, 0.99];
const SUPPORTED_RANGE_IDS = [
  DATE_RANGE_OPTIONS.TODAY.id,
  DATE_RANGE_OPTIONS.YESTERDAY.id,
  DATE_RANGE_OPTIONS.LAST_7_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_30_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_3_MONTHS.id,
  DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id,
];

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const filters = ref({
  from: 0,
  to: 0,
  userIds: [],
  labelId: null,
  inboxId: null,
  teamId: null,
  vipLevelId: null,
  issueId: null,
  priority: null,
});

const isLoading = ref(false);
const isHeatmapLoading = ref(false);
const comparisonError = ref('');
const heatmapError = ref('');
const currentPeriodSeries = ref([]);
const heatmapSeries = ref([]);
const generatedAt = ref(null);
const isBreakdownOpen = ref(true);
const heatmapWrapRef = ref(null);
const hoveredHeatmapCell = ref(null);
const latestRefreshToken = ref(0);

const createRefreshToken = () => {
  latestRefreshToken.value += 1;
  return latestRefreshToken.value;
};

const isLatestRefresh = refreshToken =>
  refreshToken === latestRefreshToken.value;

const currentPeriod = computed(() => {
  const defaultFromDate = startOfDay(subDays(new Date(), 6));
  const defaultToDate = endOfDay(new Date());
  const fromDate = filters.value.from
    ? startOfDay(fromUnixTime(filters.value.from))
    : defaultFromDate;
  const toDate = filters.value.to
    ? endOfDay(fromUnixTime(filters.value.to))
    : defaultToDate;

  return {
    from: filters.value.from || getUnixTime(fromDate),
    to: filters.value.to || getUnixTime(toDate),
    fromDate,
    toDate,
    days: Math.max(differenceInCalendarDays(toDate, fromDate) + 1, 1),
  };
});

const reportFilters = computed(() => ({
  userIds: filters.value.userIds,
  labelId: filters.value.labelId,
  teamId: filters.value.teamId,
  vipLevelId: filters.value.vipLevelId,
  issueId: filters.value.issueId,
  priority: filters.value.priority,
}));

const reportScope = computed(() => {
  const inboxId = Number(filters.value.inboxId || 0);
  if (Number.isFinite(inboxId) && inboxId > 0) {
    return { type: 'inbox', id: inboxId };
  }
  return { type: 'account', id: undefined };
});

const normalizeSeries = series => {
  if (!Array.isArray(series)) return [];
  return [...series].sort((left, right) => left.timestamp - right.timestamp);
};

const formatPeriodLabel = period => {
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
};

const currentPeriodLabel = computed(() =>
  formatPeriodLabel(currentPeriod.value)
);

const dayRows = computed(() => {
  const seriesMap = currentPeriodSeries.value.reduce((acc, item) => {
    const dayKey = format(fromUnixTime(item.timestamp), 'yyyy-MM-dd');
    acc[dayKey] = Number(item.value || 0);
    return acc;
  }, {});

  return Array.from({ length: currentPeriod.value.days }, (_, index) => {
    const date = addDays(currentPeriod.value.fromDate, index);
    const ts = getUnixTime(date);
    const dayKey = format(date, 'yyyy-MM-dd');
    return {
      timestamp: ts,
      dayKey,
      value: Number(seriesMap[dayKey] || 0),
      shortLabel: format(date, 'dd MMM'),
      tooltipLabel: format(date, "dd MMM''yy"),
      breakdownLabel: format(date, 'dd MMM yy'),
    };
  });
});

const currentMissedChats = computed(() => {
  return dayRows.value.reduce((sum, row) => sum + Number(row.value || 0), 0);
});

const VIEW_ARCHIVES_LABEL = 'View archives';

const openArchivesForDay = async row => {
  if (!row || Number(row.value || 0) <= 0) return;

  const from = Number(row.timestamp || 0);
  const to = getUnixTime(endOfDay(fromUnixTime(from)));

  await router.push({
    name: 'archives_view',
    params: {
      accountId: route.params.accountId,
    },
    query: {
      from,
      to,
      archive_filter: 'missed_chats',
    },
  });
};

const hasConversationData = computed(() =>
  dayRows.value.some(row => Number(row.value || 0) > 0)
);

const chartCollection = computed(() => ({
  labels: dayRows.value.map(row => row.shortLabel),
  datasets: [
    {
      label: t('MISSED_CHATS_REPORT.TITLE'),
      data: dayRows.value.map(row => row.value),
      backgroundColor: '#1D75E5',
      borderRadius: 4,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    },
  ],
}));

const tooltipId = 'missed-chats-tooltip';

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'nearest', intersect: true },
  onHover: buildDatasetHoverDimmingHandler(),
  onClick: (_event, elements) => {
    const activeElement = elements?.[0];
    if (!activeElement) return;

    openArchivesForDay(dayRows.value[activeElement.index]);
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
      displayColors: false,
      external: buildIntercomLikeBarTooltip({
        tooltipId,
        titleForIndex: index => dayRows.value[index]?.tooltipLabel || '',
        seriesLabelForDataset: () => t('MISSED_CHATS_REPORT.TITLE'),
        showPercent: false,
        actionLabelForIndex: index =>
          Number(dayRows.value[index]?.value || 0) > 0
            ? VIEW_ARCHIVES_LABEL
            : '',
        onActionClick: ({ dataIndex }) =>
          openArchivesForDay(dayRows.value[dataIndex]),
      }),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
    },
  },
}));

const dayColumns = computed(() => {
  return Array.from({ length: currentPeriod.value.days }, (_, index) => {
    const date = addDays(currentPeriod.value.fromDate, index);
    return {
      key: format(date, 'yyyy-MM-dd'),
      label: format(date, 'dd MMM'),
      tooltipLabel: format(date, 'dd MMM yy'),
    };
  });
});

const hourlyRows = computed(() => {
  return Array.from({ length: 24 }, (_, hour) => hour);
});

const heatmapValueMap = computed(() => {
  return heatmapSeries.value.reduce((acc, item) => {
    const date = fromUnixTime(item.timestamp);
    const dayKey = format(date, 'yyyy-MM-dd');
    const key = `${dayKey}-${date.getHours()}`;
    acc[key] = Number(item.value || 0);
    return acc;
  }, {});
});

const heatmapRows = computed(() => {
  return hourlyRows.value.map(hour => {
    return {
      hour,
      cells: dayColumns.value.map(day => {
        const key = `${day.key}-${hour}`;
        const value = heatmapValueMap.value[key] || 0;
        return {
          key,
          value,
          dayLabel: day.tooltipLabel,
        };
      }),
    };
  });
});

const heatmapValues = computed(() =>
  Object.values(heatmapValueMap.value).map(value => Number(value || 0))
);

const hasHeatmapData = computed(() =>
  heatmapValues.value.some(value => Number(value || 0) > 0)
);

const heatmapQuantiles = computed(() =>
  getQuantileIntervals(heatmapValues.value, HEATMAP_QUANTILES)
);

const heatmapLegendTicks = computed(() => {
  const max = Math.max(0, ...heatmapValues.value);
  if (max <= 0) return [];
  const base = [1, 2, 4, 6, 8, 10].filter(v => v <= max);
  if (!base.length) return [max];
  if (base[base.length - 1] !== max) base.push(max);
  return base;
});

const getHeatmapCellClass = value => {
  if (!value) return 'bg-n-slate-2';

  let level = [...heatmapQuantiles.value, Infinity].findIndex(
    range => value <= range && value > 0
  );

  if (level > 6) level = 5;
  if (level === 0) return 'bg-n-slate-2';

  const classes = [
    'bg-n-blue-3',
    'bg-n-blue-5',
    'bg-n-blue-7',
    'bg-n-blue-8',
    'bg-n-blue-10',
    'bg-n-blue-11',
  ];

  return classes[level - 1];
};

const formatHourLabel = hour => {
  const hour12 = hour % 12 || 12;
  const suffix = hour < 12 ? 'am' : 'pm';
  return `${hour12}:00 ${suffix}`;
};

const onHeatmapCellEnter = (event, cell, hour) => {
  const wrapEl = heatmapWrapRef.value;
  const target = event?.currentTarget;
  if (!wrapEl || !target) return;
  const wrapRect = wrapEl.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  hoveredHeatmapCell.value = {
    left: rect.left - wrapRect.left + rect.width / 2,
    top: rect.top - wrapRect.top,
    hour,
    dayLabel: cell.dayLabel,
    value: Number(cell.value || 0),
  };
};

const onHeatmapCellLeave = () => {
  hoveredHeatmapCell.value = null;
};

const generatedAtLabel = computed(() => {
  if (!generatedAt.value) {
    return t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET');
  }

  return generatedAt.value.toLocaleString();
});

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const downloadChartBreakdownCsv = () => {
  const headers = [
    t('TOTAL_CHATS_REPORT.CURRENT_DATE'),
    t('TOTAL_CHATS_REPORT.CURRENT_VALUE'),
  ];

  const rows = dayRows.value.map(row => [row.breakdownLabel, row.value]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'missed-chats-breakdown',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const downloadHeatmapCsv = () => {
  const headers = [
    t('TOTAL_CHATS_REPORT.HOUR'),
    ...dayColumns.value.map(day => day.label),
  ];
  const rows = heatmapRows.value.map(row => [
    formatHourLabel(row.hour),
    ...row.cells.map(cell => cell.value),
  ]);
  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'missed-chats-heatmap',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const fetchComparisonData = async refreshToken => {
  if (isLatestRefresh(refreshToken)) {
    isLoading.value = true;
    comparisonError.value = '';
  }

  try {
    const response = await reportsAPI.getReports({
      metric: METRIC_KEY,
      from: currentPeriod.value.from,
      to: currentPeriod.value.to,
      type: reportScope.value.type,
      id: reportScope.value.id,
      groupBy: 'day',
      businessHours: false,
      ...reportFilters.value,
    });

    if (!isLatestRefresh(refreshToken)) return;

    currentPeriodSeries.value = normalizeSeries(response.data);
    generatedAt.value = new Date();
  } catch {
    if (!isLatestRefresh(refreshToken)) return;
    comparisonError.value = t('MISSED_CHATS_REPORT.FETCH_ERROR');
    currentPeriodSeries.value = [];
  } finally {
    if (isLatestRefresh(refreshToken)) {
      isLoading.value = false;
    }
  }
};

const fetchHeatmapData = async refreshToken => {
  if (isLatestRefresh(refreshToken)) {
    isHeatmapLoading.value = true;
    heatmapError.value = '';
  }

  try {
    const response = await reportsAPI.getReports({
      metric: METRIC_KEY,
      from: currentPeriod.value.from,
      to: currentPeriod.value.to,
      type: reportScope.value.type,
      id: reportScope.value.id,
      groupBy: 'hour',
      businessHours: false,
      ...reportFilters.value,
    });

    if (!isLatestRefresh(refreshToken)) return;

    heatmapSeries.value = normalizeSeries(response.data);
    generatedAt.value = new Date();
  } catch {
    if (!isLatestRefresh(refreshToken)) return;
    heatmapError.value = t('MISSED_CHATS_REPORT.FETCH_ERROR');
    heatmapSeries.value = [];
  } finally {
    if (isLatestRefresh(refreshToken)) {
      isHeatmapLoading.value = false;
    }
  }
};

const refreshData = async () => {
  const refreshToken = createRefreshToken();
  await Promise.all([
    fetchComparisonData(refreshToken),
    fetchHeatmapData(refreshToken),
  ]);
};

const onFilterChange = ({
  from,
  to,
  selectedAgents = [],
  selectedLabel = null,
  selectedInbox = null,
  selectedTeam = null,
  selectedVip = null,
  selectedIssue = null,
  selectedPriority = null,
}) => {
  filters.value = {
    from,
    to,
    userIds: Array.isArray(selectedAgents)
      ? selectedAgents.map(agent => agent.id)
      : [],
    labelId: selectedLabel?.id || null,
    inboxId: selectedInbox?.id || null,
    teamId: selectedTeam?.id || null,
    vipLevelId: selectedVip?.id || null,
    issueId: selectedIssue?.id || null,
    priority: selectedPriority?.id || null,
  };
  refreshData();
};

onBeforeUnmount(() => {
  removeTooltipEl(tooltipId);
});
</script>

<template>
  <div class="mc-page">
    <div class="mc-shell">
      <div class="mc-header">
        <div class="mc-header-left">
          <h1 class="mc-title">{{ t('MISSED_CHATS_REPORT.TITLE') }}</h1>
        </div>
      </div>

      <div class="mc-toolbar">
        <div class="mc-toolbar-left">
          <CompactDateInboxFilters
            :allowed-date-range-ids="SUPPORTED_RANGE_IDS"
            show-agents
            show-labels
            show-teams
            show-vip
            show-issue
            show-priority
            hide-inactive-filters
            @filter-change="onFilterChange"
          />
        </div>
      </div>

      <div class="mc-content">
        <div v-if="comparisonError" class="mc-error">
          {{ comparisonError }}
        </div>

        <section class="mc-card">
          <div class="mc-card-header">
            <div class="mc-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="mc-card-title">
                {{ t('MISSED_CHATS_REPORT.CARD_TITLE') }}
              </h2>
              <span class="mc-period">{{ currentPeriodLabel }}</span>
            </div>

            <button
              class="mc-export-btn"
              type="button"
              @click="downloadChartBreakdownCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ t('MISSED_CHATS_REPORT.EXPORT_CSV') }}</span>
            </button>
          </div>

          <div class="mc-section mc-section--no-bottom">
            <div class="mc-big-number">
              {{ currentMissedChats.toLocaleString() }}
            </div>

            <div class="mc-chart">
              <woot-loading-state
                v-if="isLoading"
                :message="$t('REPORT.LOADING_CHART')"
                class="h-full"
              />
              <div v-else-if="!hasConversationData" class="mc-empty">
                {{ t('MISSED_CHATS_REPORT.NO_DATA') }}
              </div>
              <BarChart
                v-else
                :collection="chartCollection"
                :chart-options="chartOptions"
              />
            </div>
          </div>

          <div class="mc-breakdown">
            <div
              class="mc-breakdown-header"
              role="button"
              tabindex="0"
              @click="isBreakdownOpen = !isBreakdownOpen"
              @keydown.enter.prevent="isBreakdownOpen = !isBreakdownOpen"
              @keydown.space.prevent="isBreakdownOpen = !isBreakdownOpen"
            >
              <div class="mc-breakdown-title">
                <svg
                  class="mc-breakdown-caret"
                  :class="{ 'is-collapsed': !isBreakdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
                <span>{{ t('MISSED_CHATS_REPORT.BREAKDOWN_TITLE') }}</span>
              </div>
            </div>

            <div v-show="isBreakdownOpen" class="mc-table-wrap">
              <table class="mc-table">
                <thead>
                  <tr>
                    <th class="mc-th-series">
                      {{ t('TOTAL_CHATS_REPORT.SERIES') }}
                    </th>
                    <th
                      v-for="row in dayRows"
                      :key="`header-${row.timestamp}`"
                      class="mc-th-data"
                    >
                      <div>{{ row.breakdownLabel }}</div>
                    </th>
                    <th class="mc-th-data">
                      <div>Total</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="mc-td-series">
                      {{ t('MISSED_CHATS_REPORT.TITLE') }}
                    </td>
                    <td
                      v-for="row in dayRows"
                      :key="`row-${row.timestamp}`"
                      class="mc-td-data"
                    >
                      <div class="mc-cell-main">
                        {{ row.value.toLocaleString() }}
                      </div>
                    </td>
                    <td class="mc-td-data">
                      <div class="mc-cell-main">
                        {{ currentMissedChats.toLocaleString() }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section data-testid="heatmap-chart" class="mc-card mc-card--heatmap">
          <div class="mc-card-header">
            <div class="mc-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="mc-card-title">
                {{ t('MISSED_CHATS_REPORT.HEATMAP_TITLE') }}
              </h2>
              <span class="mc-period">{{ currentPeriodLabel }}</span>
            </div>

            <button
              class="mc-export-btn"
              type="button"
              @click="downloadHeatmapCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ t('MISSED_CHATS_REPORT.EXPORT_CSV') }}</span>
            </button>
          </div>

          <div class="mc-section">
            <p class="mc-heatmap-sub">
              {{ t('MISSED_CHATS_REPORT.HEATMAP_LIMITATION') }}
            </p>

            <div ref="heatmapWrapRef" class="mc-heatmap-wrap">
              <woot-loading-state
                v-if="isHeatmapLoading"
                :message="$t('REPORT.LOADING_CHART')"
              />

              <div v-else-if="heatmapError" class="mc-empty mc-empty--heatmap">
                {{ heatmapError }}
              </div>

              <div
                v-else-if="heatmapRows.length && hasHeatmapData"
                class="mc-heatmap-table-wrap"
              >
                <div
                  v-if="hoveredHeatmapCell"
                  class="mc-heatmap-tooltip"
                  :style="{
                    left: `${hoveredHeatmapCell.left}px`,
                    top: `${hoveredHeatmapCell.top}px`,
                  }"
                >
                  <div class="mc-heatmap-tooltip__time">
                    {{ hoveredHeatmapCell.dayLabel }}
                    {{ formatHourLabel(hoveredHeatmapCell.hour) }}
                  </div>
                  <div class="mc-heatmap-tooltip__line">
                    <span class="mc-heatmap-tooltip__dot" />
                    <span>
                      {{ t('MISSED_CHATS_REPORT.TITLE') }}:
                      {{ hoveredHeatmapCell.value.toLocaleString() }}
                    </span>
                  </div>
                </div>

                <table class="mc-heatmap-table">
                  <thead>
                    <tr>
                      <th class="mc-heatmap-th mc-heatmap-th--hour">
                        {{ t('TOTAL_CHATS_REPORT.HOUR') }}
                      </th>
                      <th
                        v-for="day in dayColumns"
                        :key="day.key"
                        class="mc-heatmap-th"
                      >
                        {{ day.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in heatmapRows" :key="`hour-${row.hour}`">
                      <td class="mc-heatmap-td mc-heatmap-td--hour">
                        {{
                          row.hour % 3 === 0 ? formatHourLabel(row.hour) : ''
                        }}
                      </td>
                      <td
                        v-for="cell in row.cells"
                        :key="cell.key"
                        class="mc-heatmap-td"
                        @mouseenter="onHeatmapCellEnter($event, cell, row.hour)"
                        @mouseleave="onHeatmapCellLeave"
                      >
                        <div
                          class="mc-heatmap-cell"
                          :class="getHeatmapCellClass(cell.value)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div v-if="heatmapLegendTicks.length" class="mc-heatmap-legend">
                  <div class="mc-heatmap-legend__bar" />
                  <div class="mc-heatmap-legend__ticks">
                    <span
                      v-for="tick in heatmapLegendTicks"
                      :key="`tick-${tick}`"
                    >
                      {{ tick }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-else class="mc-empty mc-empty--heatmap">
                {{ t('MISSED_CHATS_REPORT.NO_DATA') }}
              </div>
            </div>
          </div>
        </section>

        <p class="mc-footer-note">
          {{
            t('MISSED_CHATS_REPORT.GENERATED_AT', { time: generatedAtLabel })
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mc-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}
.mc-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
}

.mc-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}
.mc-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.mc-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
.mc-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 3px 10px -6px;
  position: sticky;
  top: 58px;
  z-index: 49;
}
.mc-toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.mc-filter-anchor {
  position: relative;
}
.mc-secondary-btn {
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  cursor: pointer;
}
.mc-secondary-btn:hover {
  background: #f9fafb;
}
.mc-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.12) 0px 14px 38px;
  padding: 6px;
  z-index: 30;
}
.mc-menu-item {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.mc-menu-item:hover {
  background: #f3f4f6;
}
.mc-menu-item.is-selected {
  background: #eef2ff;
  color: #1d4ed8;
}

.mc-menu-custom {
  margin-top: 8px;
  border-top: 1px solid #eef0f3;
  padding-top: 8px;
}

.mc-date-picker {
  width: 100%;
}

.mc-content {
  padding: 16px 32px;
}
.mc-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.mc-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #fff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}
.mc-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.mc-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.mc-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.mc-period {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
  white-space: nowrap;
}

.mc-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.mc-export-btn:hover {
  background: #f9fafb;
}

.mc-section {
  padding: 16px 20px 20px;
}
.mc-section--no-bottom {
  padding-bottom: 0;
}

.mc-big-number {
  font-size: 32px;
  line-height: 38px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.mc-metric-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 1100px) {
  .mc-metric-row {
    grid-template-columns: 1fr;
  }
}
.mc-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.mc-metric-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.mc-metric-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
}
.mc-metric-value-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.mc-metric-value {
  font-size: 28px;
  line-height: 32px;
  font-weight: 700;
  color: #111827;
}
.mc-metric-badge {
  border-radius: 6px;
  background: #e5e7eb;
  color: #374151;
  font-size: 12px;
  font-weight: 800;
  padding: 4px 6px;
}

.mc-chart {
  height: 384px;
  margin-top: 16px;
}
.mc-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}
.mc-empty--heatmap {
  height: 160px;
}

.mc-breakdown {
  border-top: 1px solid #dfe3e8;
}
.mc-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px;
  cursor: pointer;
  user-select: none;
}
.mc-breakdown-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.mc-breakdown-caret {
  transition: transform 0.18s ease;
}
.mc-breakdown-caret.is-collapsed {
  transform: rotate(-90deg);
}

.mc-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}
.mc-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  color: #424d57;
}
.mc-th-series {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
  border-right: 1px solid #edf1f5;
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #111827;
}
.mc-th-data {
  padding: 10px 12px;
  text-align: left;
  font-weight: 700;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
  background: #f8fafc;
}
.mc-th-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}
.mc-td-series {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  border-right: 1px solid #edf1f5;
  padding: 12px;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
}
.mc-td-data {
  padding: 10px 12px;
  border-bottom: 1px solid #edf1f5;
  vertical-align: top;
}
.mc-cell-main {
  font-weight: 700;
  color: #111827;
}
.mc-cell-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}
.mc-cell-diff {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 700;
}

.mc-heatmap-sub {
  margin: 0 0 12px;
  font-size: 13px;
  color: #6b7280;
}
.mc-heatmap-wrap {
  overflow-x: auto;
  position: relative;
}
.mc-heatmap-table-wrap {
  position: relative;
  overflow-x: auto;
  padding-bottom: 8px;
}
.mc-heatmap-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 2px;
}
.mc-heatmap-th {
  padding: 4px 6px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}
.mc-heatmap-th--hour {
  text-align: left;
  color: #6b7280;
}
.mc-heatmap-td {
  padding: 2px;
}
.mc-heatmap-td--hour {
  font-size: 12px;
  color: #6b7280;
  padding-right: 10px;
  white-space: nowrap;
}
.mc-heatmap-cell {
  height: 13px;
  width: 100%;
  border-radius: 2px;
}

.mc-heatmap-legend {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.mc-heatmap-legend__bar {
  width: 252px;
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #d1e2fa 0%, #4384f5 100%);
  border: 1px solid #e5e7eb;
}
.mc-heatmap-legend__ticks {
  width: 252px;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #6b7280;
}

.mc-heatmap-tooltip {
  position: absolute;
  transform: translate(-50%, calc(-100% - 10px));
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 12px;
  box-shadow: rgba(0, 0, 0, 0.18) 0px 14px 38px;
  pointer-events: none;
  min-width: 200px;
  z-index: 5;
}
.mc-heatmap-tooltip__time {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
}
.mc-heatmap-tooltip__line {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #374151;
  font-weight: 600;
}
.mc-heatmap-tooltip__dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: #cbd5e1;
  display: inline-block;
}

.mc-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

::global(body.dark) .mc-page,
::global(body.dark) .mc-shell,
::global(body.dark) .mc-card,
::global(body.dark) .mc-header,
::global(body.dark) .mc-toolbar,
::global(body.dark) .mc-th-series,
::global(body.dark) .mc-th-data,
::global(body.dark) .mc-td-series,
::global(body.dark) .mc-td-data,
::global(body.dark) .mc-heatmap-th,
::global(body.dark) .mc-heatmap-td,
::global(body.theme-mix) .mc-page,
::global(body.theme-mix) .mc-shell,
::global(body.theme-mix) .mc-card,
::global(body.theme-mix) .mc-header,
::global(body.theme-mix) .mc-toolbar,
::global(body.theme-mix) .mc-th-series,
::global(body.theme-mix) .mc-th-data,
::global(body.theme-mix) .mc-td-series,
::global(body.theme-mix) .mc-td-data,
::global(body.theme-mix) .mc-heatmap-th,
::global(body.theme-mix) .mc-heatmap-td {
  background: #fff !important;
  color: #111827 !important;
}

::global(body.dark) .mc-th-series *,
::global(body.dark) .mc-th-data *,
::global(body.dark) .mc-heatmap-th *,
::global(body.theme-mix) .mc-th-series *,
::global(body.theme-mix) .mc-th-data *,
::global(body.theme-mix) .mc-heatmap-th * {
  color: #111827 !important;
}
</style>
